import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { FilterSelect } from "@/components/ui/filter-select";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { TableSkeleton } from "@/components/Common/SkeletonLoading";

import TagBadge from "@/components/Tags/TagBadge";

import { ActionButtons } from "@/pages/Facility/settings/ActionButtons";

import useFilters from "@/hooks/useFilters";

import {
  CHARGE_ITEM_DEFINITION_STATUS_COLORS,
  ChargeItemDefinitionRead,
  ChargeItemDefinitionStatus,
} from "@/types/billing/chargeItemDefinition/chargeItemDefinition";
import chargeItemDefinitionApi from "@/types/billing/chargeItemDefinition/chargeItemDefinitionApi";
import query from "@/Utils/request/query";

// Charge item card component for mobile view
function ChargeItemCard({
  definition,
  facilityId,
}: {
  definition: ChargeItemDefinitionRead;
  facilityId: string;
}) {
  const { t } = useTranslation();

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex flex-wrap flex-col md:flex-row items-start justify-between gap-1">
          <div className="flex items-start space-x-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <Badge
                  variant={
                    CHARGE_ITEM_DEFINITION_STATUS_COLORS[definition.status]
                  }
                  className="text-xs"
                >
                  {t(definition.status)}
                </Badge>
              </div>
              <h3 className="font-medium text-gray-900 truncate text-lg font-medium">
                {definition.title}
              </h3>
              {definition.description && (
                <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                  {definition.description}
                </p>
              )}
              {definition.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {definition.tags.map((tag) => (
                    <TagBadge key={tag.id} tag={tag} className="text-xs" />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <ChargeItemDefinitionActions
              definition={definition}
              facilityId={facilityId}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Table row component for desktop view
function ChargeItemTableRow({
  definition,
  facilityId,
}: {
  definition: ChargeItemDefinitionRead;
  facilityId: string;
}) {
  const { t } = useTranslation();

  return (
    <TableRow className="hover:bg-gray-50">
      <TableCell className="font-medium">
        <div className="flex items-center space-x-3">
          <div>
            <div className="font-medium text-gray-900">{definition.title}</div>
            {definition.description && (
              <div className="text-sm text-gray-500 truncate max-w-xs">
                {definition.description}
              </div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge
          variant={CHARGE_ITEM_DEFINITION_STATUS_COLORS[definition.status]}
          className="text-xs"
        >
          {t(definition.status)}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-gray-500">
        {definition.category.title}
      </TableCell>
      <TableCell className="text-sm text-gray-500">
        {definition.price_components.length > 0
          ? `${definition.price_components.length} ${t("price_components")}`
          : t("no_price_components")}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {definition.tags.length > 0 ? (
            definition.tags.map((tag) => (
              <TagBadge key={tag.id} tag={tag} className="text-xs" />
            ))
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex justify-center gap-2">
          <ChargeItemDefinitionActions
            definition={definition}
            facilityId={facilityId}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

interface ChargeItemListProps {
  facilityId: string;
  categorySlug: string;
  setAllowCategoryCreate: (allow: boolean) => void;
}

export function ChargeItemList({
  facilityId,
  categorySlug,
  setAllowCategoryCreate,
}: ChargeItemListProps) {
  const { t } = useTranslation();
  const { qParams, updateQuery, Pagination, resultsPerPage } = useFilters({
    limit: 15,
    disableCache: true,
    defaultQueryParams: {
      status: "active",
    },
  });

  // Fetch charge items for current category
  const { data: chargeItemsResponse, isLoading: isLoadingChargeItems } =
    useQuery({
      queryKey: ["chargeItemDefinitions", facilityId, categorySlug, qParams],
      queryFn: query.debounced(
        chargeItemDefinitionApi.listChargeItemDefinition,
        {
          pathParams: { facilityId },
          queryParams: {
            title: qParams.search,
            status: qParams.status,
            category: categorySlug,
            limit: resultsPerPage,
            offset: ((qParams.page ?? 1) - 1) * resultsPerPage,
          },
        },
      ),
    });

  const chargeItems = chargeItemsResponse?.results || [];

  useEffect(() => {
    if (!qParams.search && !qParams.page) {
      setAllowCategoryCreate(!chargeItemsResponse?.count);
    }
  }, [
    chargeItemsResponse?.count,
    setAllowCategoryCreate,
    qParams.search,
    qParams.page,
  ]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative w-full sm:w-auto">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <CareIcon icon="l-search" className="size-5" />
          </span>
          <Input
            placeholder={t("search_definitions")}
            value={qParams.search || ""}
            onChange={(e) =>
              updateQuery({ search: e.target.value || undefined })
            }
            className="w-full sm:w-[300px] pl-10"
          />
        </div>

        <div className="w-full sm:w-auto">
          <FilterSelect
            value={qParams.status || ""}
            onValueChange={(value) => updateQuery({ status: value })}
            options={Object.values(ChargeItemDefinitionStatus)}
            label={t("status")}
            onClear={() => updateQuery({ status: undefined })}
          />
        </div>
      </div>

      {/* Results count */}
      {chargeItemsResponse && chargeItemsResponse.count > 0 && (
        <div className="mb-4 text-sm text-gray-600">
          {t("showing")} {chargeItems.length} {t("of")}{" "}
          {chargeItemsResponse.count} {t("charge_items")}
        </div>
      )}

      {/* Content */}
      {isLoadingChargeItems ? (
        <TableSkeleton count={5} />
      ) : chargeItems.length === 0 ? (
        <EmptyState
          icon={<CareIcon icon="l-file" className="text-primary size-6" />}
          title={t("no_charge_items_found")}
          description={t("no_charge_items_in_category")}
        />
      ) : (
        <>
          <div className="hidden lg:block">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[25%]">{t("title")}</TableHead>
                    <TableHead className="w-[10%]">{t("status")}</TableHead>
                    <TableHead className="w-[15%]">{t("category")}</TableHead>
                    <TableHead className="w-[12%]">
                      {t("price_components")}
                    </TableHead>
                    <TableHead className="w-[20%]">{t("tags")}</TableHead>
                    <TableHead className="w-[10%] text-center">
                      {t("actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chargeItems.map((definition) => (
                    <ChargeItemTableRow
                      key={definition.slug}
                      definition={definition}
                      facilityId={facilityId}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="lg:hidden">
            <div className="grid gap-3">
              {chargeItems.map((definition) => (
                <ChargeItemCard
                  key={definition.slug}
                  definition={definition}
                  facilityId={facilityId}
                />
              ))}
            </div>
          </div>

          {/* Pagination */}
          {chargeItemsResponse &&
            chargeItemsResponse.count > resultsPerPage && (
              <div className="mt-6 flex justify-center">
                <Pagination totalCount={chargeItemsResponse.count} />
              </div>
            )}
        </>
      )}
    </div>
  );
}

function ChargeItemDefinitionActions({
  definition,
  facilityId,
}: {
  definition: ChargeItemDefinitionRead;
  facilityId: string;
}) {
  return (
    <ActionButtons
      editPath={`/facility/${facilityId}/settings/charge_item_definitions/${definition.slug}/edit`}
      viewPath={`/facility/${facilityId}/settings/charge_item_definitions/${definition.slug}`}
    />
  );
}
